import React, { useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

import type { Advert } from '@/core/api/entities/campaign/advert'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { usePatchAdvert, useUploadAdvert } from '@/core/hooks/useAdvert'
import { useCreateCampaign, useUpdateCampaign } from '@/core/hooks/useCampaign'
import { useTranslation } from '@/core/i18n/useTranslation'
import type { RootState } from '@/core/store'
import {
	clearCampaignFlow,
	addAdverts,
	setAdverts,
	updateAdvert,
} from '@/core/store/slices/campaignFlowSlice'
import { useGuideTaskTracking } from '@/shared/analytics/useGuideTaskTracking'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'
import { extractErrorMessage } from '@/shared/utils/helpers'

import { CampaignDetailsStep2UI } from './CampaignDetailsStep2UI'
import {
	uploadAdSchema,
	type UploadAdFormValues,
	type PendingAdvertItem,
	type EditAdvertFormValues,
} from './schemas/createCampaignSchema'
import { generateRRule } from './utils/rruleGenerator'

const CampaignDetailsStep2: React.FC = () => {
	const { t } = useTranslation('campaigns')
	const navigate = useNavigate()
	const dispatch = useDispatch()
	const { id: routeCampaignId } = useParams<{ id?: string }>()
	const queryClient = useQueryClient()
	const { showNotification } = useNotificationContext()
	const { trackTaskById } = useGuideTaskTracking()

	// Redux state
	const { user } = useSelector((state: RootState) => state.auth)
	const account = useCurrentAccount()

	const accountId = account?.id || user?.accountId
	const [isAddExistingAdModalOpen, setIsAddExistingAdModalOpen] =
		useState(false)
	const [advertBeingEdited, setAdvertBeingEdited] = useState<Advert | null>(
		null
	)

	// Redux state
	const flowState = useSelector((state: RootState) => state.campaignFlow)

	const form = useForm<UploadAdFormValues>({
		resolver: yupResolver(uploadAdSchema) as any,
		defaultValues: {
			advertName: '',
			description: '',
			adStartDate: undefined,
			adEndDate: undefined,
			audioFile: undefined,
			pendingAdverts: [],
		},
	})

	const uploadAdvertMutation = useUploadAdvert({
		config: useAccountConfig({}),
		options: {
			onError: (error: any) => {
				showNotification({
					type: 'error',
					message: t('upload.notifications.uploadError'),
					description:
						extractErrorMessage(error) ||
						t('upload.notifications.uploadErrorDesc'),
				})
			},
		},
	})

	const patchAdvertMutation = usePatchAdvert({
		config: useAccountConfig({}),
		options: {
			onSuccess: (updatedAdvert: Advert) => {
				dispatch(updateAdvert(updatedAdvert))
				setAdvertBeingEdited(null)
				showNotification({
					type: 'success',
					message: t('modals.editAd.successTitle'),
					description: t('modals.editAd.successDesc'),
				})
			},
			onError: (error: any) => {
				showNotification({
					type: 'error',
					message: t('modals.editAd.errorTitle'),
					description:
						extractErrorMessage(error) || t('modals.editAd.errorDesc'),
				})
			},
		},
	})

	const createCampaignMutation = useCreateCampaign({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['campaigns'] })
				queryClient.invalidateQueries({ queryKey: ['adverts'] })
				dispatch(clearCampaignFlow())
				showNotification({
					type: 'success',
					message: t('upload.notifications.createSuccess'),
					description: t('upload.notifications.createSuccessDesc'),
				})
				navigate('/campaigns')
			},
			onError: (error: any) => {
				showNotification({
					type: 'error',
					message: t('upload.notifications.createError'),
					description:
						extractErrorMessage(error) ||
						t('upload.notifications.createErrorDesc'),
				})
			},
		},
	})

	const updateCampaignMutation = useUpdateCampaign({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['campaign'] })
				queryClient.invalidateQueries({ queryKey: ['campaigns'] })
				queryClient.invalidateQueries({ queryKey: ['adverts'] })
				dispatch(clearCampaignFlow())
				showNotification({
					type: 'success',
					message: t('upload.notifications.updateSuccess'),
					description: t('upload.notifications.updateSuccessDesc'),
				})
				navigate('/campaigns')
			},
			onError: (error: any) => {
				showNotification({
					type: 'error',
					message: t('upload.notifications.updateError'),
					description:
						extractErrorMessage(error) ||
						t('upload.notifications.createErrorDesc'),
				})
			},
		},
	})

	const handleEditAdvert = (advert: Advert) => {
		setAdvertBeingEdited(advert)
	}

	const handleSaveEditedAdvert = (
		advertId: number,
		values: EditAdvertFormValues
	) => {
		patchAdvertMutation.mutate({
			advertId,
			data: {
				name: values.advertName,
				description: values.description || null,
				date_start: values.adStartDate
					? DateTime.fromJSDate(values.adStartDate).toFormat('yyyy-MM-dd')
					: null,
				date_end: values.adEndDate
					? DateTime.fromJSDate(values.adEndDate).toFormat('yyyy-MM-dd')
					: null,
			},
		})
	}

	const handleExistingAdd = () => {
		setIsAddExistingAdModalOpen(true)
	}

	const handleConfirmExistingAds = async (selectedAdverts: Advert[]) => {
		if (!flowState.config) return

		const existingIds = flowState.advertIds ?? []
		const newIds = selectedAdverts.map((ad) => ad.id)
		const mergedIds = Array.from(new Set([...existingIds, ...newIds]))

		const existingAdverts = flowState.adverts ?? []
		const mergedAdvertsMap = new Map<number, Advert>()
		for (const ad of existingAdverts) mergedAdvertsMap.set(ad.id, ad)
		for (const ad of selectedAdverts) mergedAdvertsMap.set(ad.id, ad)

		// Update Redux state
		dispatch(
			addAdverts({
				advertIds: newIds,
				adverts: selectedAdverts,
			})
		)

		// If we are editing an existing campaign, immediately persist the new
		// adverts selection to the backend and return to the campaigns list.
		if (flowState.campaignId) {
			await updateCampaignWithAdverts(mergedIds)
		}
	}

	const handleRemoveAdvert = async (advertId: number) => {
		const currentAdverts = flowState.adverts || []
		const currentAdvertIds = flowState.advertIds || []

		const newAdverts = currentAdverts.filter((ad) => ad.id !== advertId)
		const newAdvertIds = currentAdvertIds.filter((id) => id !== advertId)

		dispatch(
			setAdverts({
				advertIds: newAdvertIds,
				adverts: newAdverts,
			})
		)
	}

	const handleAddCurrentToPending = () => {
		const values = form.getValues()
		if (!values.audioFile) return
		const name = (values.advertName || '').trim()
		if (name.length < 6) {
			form.setError('advertName', {
				type: 'min',
				message: 'Advert name must be at least 6 characters',
			})
			window.scrollTo({ top: 0, behavior: 'smooth' })
			document
				.getElementById('campaign-upload-mobile-form')
				?.scrollTo({ top: 0, behavior: 'smooth' })
			return
		}
		const pending: PendingAdvertItem = {
			audioFile: values.audioFile,
			advertName: name,
			description: values.description ?? undefined,
			adStartDate: values.adStartDate ?? undefined,
			adEndDate: values.adEndDate ?? undefined,
		}
		form.setValue('pendingAdverts', [...(values.pendingAdverts || []), pending])
		form.setValue('audioFile', undefined)
		form.setValue('advertName', '')
		form.setValue('description', '')
		form.setValue('adStartDate', undefined)
		form.setValue('adEndDate', undefined)
		form.clearErrors('advertName')
	}

	const handleRemovePendingAdvert = (index: number) => {
		const current = form.getValues('pendingAdverts') || []
		form.setValue(
			'pendingAdverts',
			current.filter((_, i) => i !== index)
		)
	}

	const handleMultipleFilesAttempt = () => {
		showNotification({
			type: 'info',
			message: t('upload.notifications.singleFileOnly'),
			description: t('upload.notifications.singleFileOnlyDesc'),
		})
	}

	const updateCampaignWithAdverts = async (advertIds: number[]) => {
		if (!flowState.config || !flowState.campaignId || !accountId) return

		const {
			config,
			campaignId: existingCampaignId,
			accountId: flowAccountId,
		} = flowState

		const rrule = generateRRule({
			rruleType: config.rruleType,
			playEvery: config.playEvery,
			playEveryUnit: config.playEveryUnit,
			selectedDays: (config.selectedDays || []).filter(
				(d): d is 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU' =>
					d !== undefined
			),
			timeFrom: config.timeFrom,
			timeTo: config.timeTo,
			startDate: config.startDate || undefined,
			endDate: config.endDate || undefined,
		})

		const type = config.interruptsMusic ? 'opening' : 'other'

		try {
			await updateCampaignMutation.mutateAsync({
				id: existingCampaignId,
				data: {
					name: config.name,
					rrule,
					rrule_type: config.rruleType,
					type,
					priority: config.priority,
					exdate: config.exdate || [],
					adverts: advertIds.map((id) => ({ id })),
					account_id: flowAccountId ?? accountId,
				},
			})
		} catch (error) {
			console.error('Error updating campaign adverts:', error)
		}
	}

	const onSubmit = async (values: UploadAdFormValues) => {
		try {
			if (!flowState.config) {
				showNotification({
					type: 'error',
					message: t('upload.notifications.missingConfig'),
					description: t('upload.notifications.missingConfigDesc'),
				})
				navigate('/campaigns')
				return
			}

			const {
				mode,
				config,
				campaignId: existingCampaignId,
				advertIds,
				accountId: flowAccountId,
			} = flowState

			if (!accountId) {
				showNotification({
					type: 'error',
					message: t('upload.notifications.accountError'),
					description: t('upload.notifications.accountErrorDesc'),
				})
				return
			}

			const selectedAdvertIds = advertIds || []
			const pending = values.pendingAdverts || []

			// Allow submit with only existing selected ads (e.g. from "Select existing")
			if (pending.length === 0 && selectedAdvertIds.length === 0) {
				showNotification({
					type: 'error',
					message: t('upload.notifications.noAds'),
					description: t('upload.notifications.noAdsDesc'),
				})
				return
			}

			const uploadedIds: number[] = []
			for (const item of pending) {
				const formData = new FormData()
				formData.append('media', item.audioFile)
				formData.append('name', item.advertName)
				if (item.description) {
					formData.append('description', item.description)
				}
				if (item.adStartDate) {
					formData.append(
						'date_start',
						DateTime.fromJSDate(item.adStartDate).toFormat('yyyy-MM-dd')
					)
				}
				if (item.adEndDate) {
					formData.append(
						'date_end',
						DateTime.fromJSDate(item.adEndDate).toFormat('yyyy-MM-dd')
					)
				}
				const uploadResponse = await uploadAdvertMutation.mutateAsync(formData)
				uploadedIds.push(uploadResponse.data.data.id)
				trackTaskById('create-advert')
			}

			const finalAdvertIds = Array.from(
				new Set([...selectedAdvertIds, ...uploadedIds])
			)

			const rrule = generateRRule({
				rruleType: config.rruleType,
				playEvery: config.playEvery,
				playEveryUnit: config.playEveryUnit,
				selectedDays: (config.selectedDays || []).filter(
					(d): d is 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA' | 'SU' =>
						d !== undefined
				),
				timeFrom: config.timeFrom,
				timeTo: config.timeTo,
				startDate: config.startDate || undefined,
				endDate: config.endDate || undefined,
			})

			// Debug: log the exact RRULE being sent
			console.log('Submitting campaign with RRULE:', {
				mode,
				campaignId: existingCampaignId,
				rruleType: config.rruleType,
				rrule,
			})

			const type = config.interruptsMusic ? 'opening' : 'other'

			if (mode === 'create') {
				const payload = {
					name: config.name,
					rrule,
					rrule_type: config.rruleType,
					type,
					priority: config.priority,
					exdate: config.exdate || [],
					adverts: finalAdvertIds.map((id) => ({ id })),
					account_id: accountId,
				}

				await createCampaignMutation.mutateAsync(payload)
			} else if (mode === 'edit' && existingCampaignId) {
				const payload = {
					name: config.name,
					rrule,
					rrule_type: config.rruleType,
					type,
					priority: config.priority,
					exdate: config.exdate || [],
					adverts: finalAdvertIds.map((id) => ({ id })),
					account_id: flowAccountId ?? accountId!,
				}

				await updateCampaignMutation.mutateAsync({
					id: existingCampaignId,
					data: payload,
				})
			}
		} catch (error) {
			console.error('Error in campaign creation flow:', error)
		}
	}

	const handleBack = () => {
		if (flowState.mode === 'edit' && routeCampaignId) {
			navigate(`/campaigns/${routeCampaignId}/edit`)
		} else {
			navigate('/campaigns/create')
		}
	}

	const clearCurrentDraftAdvert = () => {
		form.setValue('audioFile', undefined)
		form.setValue('advertName', '')
		form.setValue('description', '')
		form.setValue('adStartDate', undefined)
		form.setValue('adEndDate', undefined)
		form.clearErrors()
	}

	const handleCancel = () => {
		const pendingAdverts = form.getValues('pendingAdverts') || []
		const hasCurrentUpload = Boolean(form.getValues('audioFile'))
		const shouldDiscardCurrentDraftOnly =
			pendingAdverts.length > 0 && hasCurrentUpload

		if (shouldDiscardCurrentDraftOnly) {
			clearCurrentDraftAdvert()
			return
		}

		handleBack()
	}

	const isSubmitting =
		uploadAdvertMutation.isPending ||
		createCampaignMutation.isPending ||
		updateCampaignMutation.isPending

	const isEditingSaving = patchAdvertMutation.isPending

	const title =
		flowState.mode === 'edit'
			? t('upload.updateTitle')
			: t('upload.createTitle')

	return (
		<CampaignDetailsStep2UI
			title={title}
			form={form}
			adverts={flowState.adverts}
			pendingAdverts={(form.watch('pendingAdverts') || []).map(
				(item) =>
					({
						...item,
						description: item.description ?? undefined,
						adStartDate: item.adStartDate ?? undefined,
						adEndDate: item.adEndDate ?? undefined,
					}) as PendingAdvertItem
			)}
			isSubmitting={isSubmitting}
			isAddExistingAdModalOpen={isAddExistingAdModalOpen}
			onOpenChangeExistingAdModal={setIsAddExistingAdModalOpen}
			onExistingAdd={handleExistingAdd}
			onConfirmExistingAds={handleConfirmExistingAds}
			selectedAdvertIds={flowState.advertIds}
			onSubmit={onSubmit}
			onBack={handleBack}
			onCancel={handleCancel}
			onRemoveAdvert={handleRemoveAdvert}
			onAddCurrentToPending={handleAddCurrentToPending}
			onRemovePendingAdvert={handleRemovePendingAdvert}
			onMultipleFilesAttempt={handleMultipleFilesAttempt}
			advertBeingEdited={advertBeingEdited}
			isEditingSaving={isEditingSaving}
			onEditAdvert={handleEditAdvert}
			onOpenChangeEditAdModal={(open) => {
				if (!open) setAdvertBeingEdited(null)
			}}
			onSaveEditedAdvert={handleSaveEditedAdvert}
		/>
	)
}

export default CampaignDetailsStep2
